/**
 * StepAgentDetailPanel
 *
 * A full-width Dialog that shows agent run details for a specific agent step in a workflow.
 * Three-column layout:
 *   Left  (220px) — run history list for the agent
 *   Center (flex)  — prompt + response rendered as markdown
 *   Right (220px) — artifacts sidebar
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import ArtifactsSideBar from "./ArtifactsSideBar";
import type { Artifact } from "./ArtifactsSideBar";
import {
  listAllAgentRuns,
  getAgentRunResult,
  getAgentRunArtifacts,
} from "../../api/agentApi";

// ─── helpers ─────────────────────────────────────────────────────────────────

const runStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "success":
      return "#4caf50";
    case "failed":
    case "error":
      return "#f44336";
    case "running":
      return "#2196f3";
    default:
      return "#9e9e9e";
  }
};

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// ─── component ────────────────────────────────────────────────────────────────

export interface StepAgentDetailPanelProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentVersion?: number;
  /** Pinned run number from the workflow step execution (shown first) */
  initialRunNumber?: number;
  stepLabel: string;
  /** Filter run history to runs triggered by this specific workflow */
  workflowId?: string;
  workflowVersion?: number;
}

const StepAgentDetailPanel: React.FC<StepAgentDetailPanelProps> = ({
  open,
  onClose,
  agentId,
  agentVersion,
  initialRunNumber,
  stepLabel,
  workflowId,
  workflowVersion,
}) => {
  const [runs, setRuns] = useState<any[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<any | null>(null);

  const [prompt, setPrompt] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(true);

  // Mock color constants for clarity
  const PRIMARY_COLOR = "#B87333"; // AgentFlow Copper
  const BG_SUBTLE = "#F8FAFC"; // Slate 50
  const BORDER_COLOR = "#E2E8F0"; // Slate 200

  // ── fetch run list ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !agentId) return;
    setRunsLoading(true);
    (listAllAgentRuns as any)(agentId, {
      page: 1,
      page_size: 50,
      version: agentVersion ?? null,
      triggered_by_workflow: true,
      workflow_id: workflowId ?? null,
      workflow_version: workflowVersion ?? null,
    })
      .then((res: any) => {
        const list: any[] = res.runs ?? [];
        setRuns(list);
        // Select the pinned run first, or the most recent one
        const pinned =
          initialRunNumber != null
            ? list.find((r) => r.run_number === initialRunNumber)
            : null;
        const target = pinned ?? list[0] ?? null;
        setSelectedRun(target);
      })
      .catch(console.error)
      .finally(() => setRunsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId, agentVersion]);

  // ── fetch detail whenever selected run changes ──────────────────────────
  const loadDetail = useCallback(
    async (run: any) => {
      if (!run) {
        setPrompt("");
        setResponse("");
        setArtifacts([]);
        return;
      }
      setDetailLoading(true);
      try {
        // Prompt is stored directly on the run object — parse JSON wrapper if present
        console.debug("[StepAgentDetailPanel] run object:", run);
        const rawPrompt = run.agent_prompt ?? "";
        let resolvedPrompt = typeof rawPrompt === "string" ? rawPrompt : JSON.stringify(rawPrompt, null, 2);
        try {
          const parsed = JSON.parse(resolvedPrompt);
          if (parsed && typeof parsed._nm_input === "string") resolvedPrompt = parsed._nm_input;
          else if (parsed && typeof parsed.input === "string") resolvedPrompt = parsed.input;
        } catch { /* not JSON, use as-is */ }
        console.debug("[StepAgentDetailPanel] resolvedPrompt:", JSON.stringify(resolvedPrompt));
        setPrompt(resolvedPrompt);

        const [resultData, artifactsData] = await Promise.all([
          getAgentRunResult(
            run.agent_id ?? agentId,
            run.agent_version ?? agentVersion,
            run.run_number,
          ),
          getAgentRunArtifacts(
            run.agent_id ?? agentId,
            run.agent_version ?? agentVersion,
            run.run_number,
          ),
        ]);

        // response
        const rawResult = resultData?.output ?? "";
        setResponse(
          typeof rawResult === "string"
            ? rawResult
            : JSON.stringify(rawResult, null, 2),
        );

        // artifacts
        const rawArts: any[] = artifactsData?.artifacts ?? [];
        setArtifacts(
          rawArts.map((a: any, i: number) => ({
            id: a.id ?? `art-${i}`,
            name: a.name ?? a.filename ?? "File",
            filename: a.filename ?? a.name ?? "file",
            type: a.type ?? "",
            url: a.url ?? "",
            agent_id: run.agent_id ?? agentId,
            agent_version: run.agent_version ?? agentVersion,
            run_number: run.run_number,
          })),
        );
      } catch (err) {
        console.error("Failed to load agent run detail", err);
        setPrompt("");
        setResponse("Failed to load run details.");
        setArtifacts([]);
      } finally {
        setDetailLoading(false);
      }
    },
    [agentId, agentVersion],
  );

  useEffect(() => {
    if (open) { loadDetail(selectedRun); setIsPromptExpanded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRun, open]);

  const handleClose = () => {
    setRuns([]);
    setSelectedRun(null);
    setPrompt("");
    setResponse("");
    setArtifacts([]);
    onClose();
  };

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "92vh",
          borderRadius: "16px", // Softer corners
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      {/* Header - Modern Glassmorphism effect */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          py: 2,
          px: 3,
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(8px)",
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: `${PRIMARY_COLOR}15`,
          }}
        >
          <SmartToyIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            flex: 1,
            fontSize: "1.1rem",
            color: "#1e293b",
          }}
        >
          {stepLabel}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          overflow: "hidden",
          height: "100%",
          bgcolor: BG_SUBTLE,
        }}
      >
        {/* ── Left: Run History (Sidebar Redesign) ───────────────────── */}
        <Box
          sx={{
            width: "20%",
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "white", // White sidebar on subtle background
            borderRight: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                color: "text.disabled",
                letterSpacing: 1.2,
              }}
            >
              Run History
            </Typography>
          </Box>

          {runsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 1,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.05)",
                  borderRadius: "10px",
                },
              }}
            >
              {runs.map((run) => {
                const isSelected = selectedRun?.run_number === run.run_number;
                const isPinned = run.run_number === initialRunNumber;
                return (
                  <ListItem
                    key={run.run_number}
                    disablePadding
                    sx={{ mb: 0.5 }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => setSelectedRun(run)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: "10px",
                        transition: "all 0.2s",
                        mx: 0.5,
                        "&.Mui-selected": {
                          bgcolor: `${PRIMARY_COLOR}10`,
                          borderLeft: `4px solid ${PRIMARY_COLOR}`,
                          "&:hover": { bgcolor: `${PRIMARY_COLOR}15` },
                        },
                      }}
                    >
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? PRIMARY_COLOR : "#475569",
                            }}
                          >
                            Run #{run.run_number}
                          </Typography>
                          {isPinned && (
                            <Chip
                              label="current"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                bgcolor: PRIMARY_COLOR,
                                color: "white",
                              }}
                            />
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                           <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: runStatusColor(run.status), flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                              {run.status?.toLowerCase() ?? "unknown"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.7rem",
                              fontWeight: 500,
                            }}
                          >
                            {fmt(run.started_at ?? run.created_at)}
                          </Typography>
                        </Box>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* ── Center: Main Content (Bento Style) ──────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {detailLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : selectedRun ? (
            <>
              {/* Prompt Card — collapsible, blue-tinted */}
              <Box
                  sx={{
                    bgcolor: "#EFF6FF",
                    borderRadius: "12px",
                    border: "1px solid #BFDBFE",
                  }}
                >
                  {/* Clickable header */}
                  <Box
                    onClick={() => setIsPromptExpanded((v) => !v)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 0.5,
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { bgcolor: "#DBEAFE" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 800, color: "#1D4ED8", letterSpacing: 1 }}
                      >
                        User Intent
                      </Typography>
                      {!isPromptExpanded && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#3B82F6", fontSize: "0.75rem", fontStyle: "italic" }}
                        >
                          {prompt ? `${prompt.slice(0, 60)}${prompt.length > 60 ? "…" : ""}` : "No prompt recorded"}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" sx={{ color: "#3B82F6", p: 0.25 }}>
                      {isPromptExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>

                  {/* Collapsible body */}
                  {isPromptExpanded && (
                    <Box
                      sx={{
                        px: 2,
                        pt: 0.5,
                        pb: 0.5,
                        fontSize: "0.88rem",
                        color: "#1E3A5F",
                        lineHeight: 1.6,
                        "& p": { mb: 1, mt: 0 },
                        "& ul, & ol": { pl: 3, mb: 1 },
                        "& li": { mb: 0.25 },
                        "& strong": { color: "#1E40AF", fontWeight: 700 },
                        "& code": {
                          fontFamily: "'JetBrains Mono', monospace",
                          bgcolor: "#DBEAFE",
                          px: 0.6, py: 0.2,
                          borderRadius: "4px",
                          fontSize: "0.85em",
                          color: "#1D4ED8",
                        },
                        "& pre": {
                          bgcolor: "#DBEAFE",
                          p: 1.5, borderRadius: "8px",
                          overflow: "auto",
                          fontSize: "0.83rem",
                          fontFamily: "'JetBrains Mono', monospace",
                        },
                      }}
                    >
                      {prompt
                        ? <Typography variant="body2" sx={{ color: "#1E3A5F", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, fontSize: "0.88rem" }}>
                            {prompt}
                          </Typography>
                        : <Typography variant="body2" sx={{ fontStyle: "italic", color: "#93C5FD" }}>No prompt recorded for this run.</Typography>
                      }
                    </Box>
                  )}
                </Box>

              {/* Response Card — green-tinted */}
              <Box
                sx={{
                  bgcolor: "#F0FDF4",
                  borderRadius: "12px",
                  border: "1px solid #BBF7D0",
                }}
              >
                <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #BBF7D0" }}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, color: "#15803D", letterSpacing: 1 }}
                  >
                    Agent Response
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 3, py: 2.5,
                    fontSize: "0.95rem",
                    color: "#14532D",
                    lineHeight: 1.8,
                    "& h1, & h2": {
                      borderBottom: "1px solid #BBF7D0",
                      pb: 1, mt: 3, mb: 2,
                      color: "#052e16",
                    },
                    "& p": { mb: 2 },
                    "& ul": { mb: 2, pl: 3 },
                    "& strong": { color: "#052e16" },
                    "& code": {
                      bgcolor: "#DCFCE7",
                      px: 0.8, py: 0.3,
                      borderRadius: "4px",
                      color: "#166534",
                      fontSize: "0.9em",
                    },
                  }}
                >
                  {response ? (
                    <ReactMarkdown>{response}</ReactMarkdown>
                  ) : (
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "#4ADE80" }}>
                      Empty response.
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                color: "text.disabled",
              }}
            >
              <Typography>Select a run to view details</Typography>
            </Box>
          )}
        </Box>

        {/* ── Right: Artifacts ────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "white",
            borderLeft: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <ArtifactsSideBar artifacts={artifacts} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StepAgentDetailPanel;
